import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  SelectChangeEvent,
  CircularProgress,
  ListSubheader,
} from '@mui/material';
import {
  Store as StoreIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useLocations } from '../hooks/useAnalytics';

interface LocationSelectorProps {
  value: string;
  onChange: (locationId: string) => void;
  multiple?: boolean;
  selectedBrand?: string;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({ value, onChange, multiple = false, selectedBrand }) => {
  const { data, isLoading, error } = useLocations();

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  if (error) {
    return (
      <FormControl fullWidth disabled>
        <InputLabel>Error loading locations</InputLabel>
        <Select value="" />
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth>
      <InputLabel id="location-selector-label">
        {isLoading ? 'Loading locations...' : 'Select Location'}
      </InputLabel>
      <Select
        labelId="location-selector-label"
        value={value}
        onChange={handleChange}
        label="Select Location"
        startAdornment={
          isLoading ? (
            <CircularProgress size={20} sx={{ mr: 1 }} />
          ) : (
            <StoreIcon sx={{ mr: 1, color: 'text.secondary' }} />
          )
        }
      >
        <MenuItem value="all">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon />
            <span>All Locations</span>
            {data?.count && (
              <Chip
                label={`${data.count} stores`}
                size="small"
                sx={{ ml: 'auto' }}
              />
            )}
          </Box>
        </MenuItem>

        {data?.byRegion && Object.entries(data.byRegion).flatMap(([region, locations]) => {
          const locationArray = locations as any[];
          // Filter by brand if selected
          const filteredLocations = selectedBrand && selectedBrand !== 'all' 
            ? locationArray.filter(loc => loc.brand === selectedBrand)
            : locationArray;
            
          if (filteredLocations.length === 0) return [];
          
          return [
            <ListSubheader key={`header-${region}`}>
              {region} ({filteredLocations.length} stores)
            </ListSubheader>,
            ...filteredLocations.map((location: any) => (
              <MenuItem key={location.id} value={location.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box>{location.name}</Box>
                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {location.city}, {location.state}
                    </Box>
                    {location.brand && location.brand !== 'Savvy Sliders' && (
                      <Chip 
                        label={location.brand} 
                        size="small" 
                        sx={{ 
                          mt: 0.5,
                          backgroundColor: 'rgba(175, 35, 38, 0.1)',
                          color: 'primary.main'
                        }} 
                      />
                    )}
                  </Box>
                  {location.isActive ? (
                    <Chip label="Active" size="small" color="success" sx={{ ml: 2 }} />
                  ) : (
                    <Chip label="Inactive" size="small" sx={{ ml: 2 }} />
                  )}
                </Box>
              </MenuItem>
            ))
          ];
        })}
      </Select>
    </FormControl>
  );
};

export default LocationSelector;